import { safeJSONParse } from "./helper";

/**
 * Agent Response Parser Utility
 * Replicates the exact parsing logic from userSlice.ts for consistency
 */

/**
 * HR Workflow endpoints - matches userSlice.ts line 358-361
 * Used to determine if response should be parsed as HR workflow or campaign workflow
 */
const HR_WORKFLOW_ENDPOINTS = [
  "/stream/hr-workflow",
  "/stream/hr-inference",
  "/stream/jd-composer",
  "/stream/hr-screening",
  "/stream/agent_cv_screener",
  "/hr-workflow",
  "/hr-inference",
  "/jd-composer",
  "/hr-screening",
  "/agent_cv_screener",
];

/**
 * Check if endpoint is an HR workflow endpoint
 * @param endpoint - Agent endpoint
 * @returns true if HR workflow endpoint
 */
export function isHRWorkflowEndpoint(endpoint?: string): boolean {
  if (!endpoint) return false;
  return HR_WORKFLOW_ENDPOINTS.includes(endpoint);
}

/**
 * Parse campaign workflow response - exact replica of userSlice logic
 * @param payload - The payload object (historical chat has data directly, not result.data)
 * @returns Parsed response with finalAnswer and other agent-specific data
 */
export function parseCampaignWorkflowResponse(payload: any): any {
  // Get the actual data from payload
  // Historical chat: payload.data directly
  // Live chat: payload.result.data
  const payloadData = payload?.data || payload?.result?.data;
  const agentIdName = payloadData?.agent_id;

  const lastAgentId = payloadData?.agent_id;
  const question = payloadData?.question;
  const agentResponse = payloadData?.response;

  // Initialize response structure - matches userSlice.ts lines 614-624
  let finalResponse: any = {
    finalAnswer: "",
    brandReviewerAnswer: "",
    twitterAnswer: "",
    linkedinAnswer: "",
    instagramAnswer: "",
    images: "",
    socialMediaImages: "",
    socialMediaLocalizer: "",
    question: question,
  };

  // Utility Agent - matches userSlice.ts lines 626-631
  if (lastAgentId === "utility_agent") {
    finalResponse = {
      ...finalResponse,
      finalAnswer: agentResponse[0]?.result,
    };
  } else {
    // Press Release Agent - matches userSlice.ts lines 634-648
    if (lastAgentId === "press_release_agent") {
      const rawBrand = [...agentResponse].find(
        (item: any) => item.agent_id === "BrandReviewerAgent",
      )?.result;
      const rawPress = [...agentResponse].find(
        (item: any) => item.agent_id === "PressReleaseAgent",
      )?.result;
      const brandParsed = safeJSONParse(rawBrand);
      const pressParsed = safeJSONParse(rawPress);
      finalResponse = {
        ...finalResponse,
        finalAnswer: pressParsed,
        brandReviewerAnswer: brandParsed,
      };
    }
    // Social Media Insights Agent - matches userSlice.ts lines 649-711
    else if (lastAgentId === "social_media_insights_agent") {
      const socialMediaImages = payloadData?.images;
      const rawTwitter = [...agentResponse].find(
        (item: any) => item.agent_id === "TwitterAgent",
      )?.result;
      const rawLinkedin = [...agentResponse].find(
        (item: any) => item.agent_id === "LinkedinAgent",
      )?.result;
      const rawInstagram = [...agentResponse].find(
        (item: any) => item.agent_id === "InstagramAgent",
      )?.result;
      const rawInsights = [...agentResponse].find(
        (item: any) => item.agent_id === "SocialMediaInsightsAgent",
      )?.result;
      const rawTwitterImages = [...agentResponse].find(
        (item: any) => item.agent_id === "twitter_image",
      )?.result;
      const rawLinkedinImages = [...agentResponse].find(
        (item: any) => item.agent_id === "linkedin_image",
      )?.result;
      const rawInstagramImages = [...agentResponse].find(
        (item: any) => item.agent_id === "instagram_image",
      )?.result;
      const rawTwitterLoc = [...agentResponse].find(
        (item: any) => item.agent_id === "SocialMediaLocalizerAgent_twitter",
      )?.result;
      const rawLinkedinLoc = [...agentResponse].find(
        (item: any) => item.agent_id === "SocialMediaLocalizerAgent_linkedin",
      )?.result;
      const rawInstagramLoc = [...agentResponse].find(
        (item: any) => item.agent_id === "SocialMediaLocalizerAgent_instagram",
      )?.result;

      const images = {
        rawTwitterImages,
        rawLinkedinImages,
        rawInstagramImages,
      };
      const socialMediaLocalizer = {
        rawTwitterLoc,
        rawLinkedinLoc,
        rawInstagramLoc,
      };
      const twitterParsed = safeJSONParse(rawTwitter);
      const linkedinParsed = safeJSONParse(rawLinkedin);
      const instagramParsed = safeJSONParse(rawInstagram);
      const insightsParsed = safeJSONParse(rawInsights);
      const imagesParsed = safeJSONParse(images);
      const socialMediaImagesParsed = safeJSONParse(socialMediaImages);
      const socialMediaLocalizerParsed = safeJSONParse(socialMediaLocalizer);

      finalResponse = {
        ...finalResponse,
        finalAnswer: insightsParsed,
        twitterAnswer: twitterParsed,
        linkedinAnswer: linkedinParsed,
        instagramAnswer: instagramParsed,
        images: imagesParsed,
        socialMediaImages: socialMediaImagesParsed,
        socialMediaLocalizer: socialMediaLocalizerParsed,
      };
    }
    // Translation Agent - matches userSlice.ts lines 712-717
    else if (lastAgentId === "translation_agent") {
      const lastParsed = safeJSONParse(agentResponse);
      finalResponse = {
        ...finalResponse,
        finalAnswer: lastParsed,
      };
    }
    // Consolidator Agent - matches userSlice.ts lines 718-766
    else if (lastAgentId === "consolidator_agent") {
      // Check if this is a QA agent response
      const hasQAAgent = agentResponse?.some(
        (item: any) => item.agent_id === "contract_reviewer_qa_agent",
      );

      if (hasQAAgent) {
        // QA response scenario: preserve the response array
        finalResponse = {
          ...finalResponse,
          response: agentResponse,
        };
      } else {
        // Normal consolidator response
        let consolidatedSummary;

        // Check if this is an update (single item) or initial response (two items)
        if (
          agentResponse?.length === 1 &&
          agentResponse[0]?.agent_id === "ConsolidatorAgent"
        ) {
          // Update scenario: only ConsolidatorAgent in response
          consolidatedSummary = agentResponse[0]?.result || {};
        } else if (agentResponse?.length === 2) {
          // Initial scenario: both individual reviews and consolidated summary
          consolidatedSummary = agentResponse[1]?.result || {};
        } else {
          // Fallback: try to find ConsolidatorAgent in the array
          const consolidatorItem = agentResponse?.find(
            (item: any) => item.agent_id === "ConsolidatorAgent",
          );
          consolidatedSummary = consolidatorItem?.result || {};
        }

        // Show only consolidated data in the response
        finalResponse = {
          ...finalResponse,
          finalAnswer: consolidatedSummary,
        };
      }
    }
    // Template Reviewer Agent - matches userSlice.ts lines 767-774
    else if (lastAgentId === "template_reviewer_agent") {
      const rawLast = agentResponse[0];
      const lastParsed = safeJSONParse(rawLast?.result);
      finalResponse = {
        ...finalResponse,
        finalAnswer: lastParsed,
      };
    }
    // NDA orchestrator HITL — preserve question + full response array (N5-01 input review)
    else if (
      lastAgentId === "nda_orchestrator" ||
      lastAgentId === "nda_intake_agent" ||
      lastAgentId === "nda_input_review_agent" ||
      lastAgentId === "nda_counterparty_agent" ||
      lastAgentId === "nda_conflict_agent" ||
      lastAgentId === "nda_template_selector_agent" ||
      lastAgentId === "nda_draft_field_capture_agent" ||
      lastAgentId === "nda_drafting_agent" ||
      lastAgentId === "nda_validation_agent" ||
      lastAgentId === "nda_reviewer_agent" ||
      lastAgentId === "nda_parser_agent" ||
      lastAgentId === "nda_qa_agent"
    ) {
      if (
        payloadData &&
        Object.prototype.hasOwnProperty.call(payloadData, "question")
      ) {
        finalResponse = {
          ...finalResponse,
          finalAnswer: payloadData,
        };
      } else if (
        Array.isArray(agentResponse) &&
        agentResponse.length > 0
      ) {
        const ndaResult =
          agentResponse.find((item: any) => item.agent_id === lastAgentId) ||
          agentResponse[agentResponse.length - 1];
        const ndaParsed = safeJSONParse(ndaResult?.result);
        finalResponse = {
          ...finalResponse,
          finalAnswer: ndaParsed,
        };
      }
    }
    // RFQ Orchestrator - matches userSlice.ts lines 775-788
    else if (lastAgentId === "rfq_orchestrator") {
      const orchestratorResult = agentResponse?.find(
        (item: any) => item.agent_id === "rfq_orchestrator",
      );

      finalResponse = {
        ...finalResponse,
        finalAnswer: {
          ...orchestratorResult?.result,
          response: agentResponse, // Preserve full response array including rfq_qa_agent
        },
      };
    }
    // Default - matches userSlice.ts lines 789-803
    else {
      if (
        agentResponse &&
        Array.isArray(agentResponse) &&
        agentResponse.length > 0
      ) {
        const rawLast = agentResponse[agentResponse.length - 1];
        const lastParsed = safeJSONParse(rawLast?.result);
        finalResponse = {
          ...finalResponse,
          finalAnswer: lastParsed,
        };
      }
    }
  }

  return finalResponse;
}

/**
 * Parse HR workflow response
 * Expects structure: { data: { output, prompt, step_num, agent_name } }
 * This matches the payload structure from historical chat API
 *
 * @param payload - Payload object with data.output, data.prompt, data.step_num
 * @returns Parsed HR workflow response with answer, question, stepNumber
 */
export function parseHRWorkflowResponse(payload: any): any {
  console.log("parseHRWorkflowResponse received:", payload);
  console.log("HR workflow data:", {
    hasData: !!payload?.data,
    dataKeys: payload?.data ? Object.keys(payload.data) : [],
    output: payload?.data?.output,
    prompt: payload?.data?.prompt,
    step_num: payload?.data?.step_num,
    agent_name: payload?.data?.agent_name,
  });

  const answer = payload?.data?.output;
  const question = payload?.data?.prompt;
  const stepNumber = payload?.data?.step_num;

  const result = {
    answer,
    question,
    stepNumber,
  };

  console.log("parseHRWorkflowResponse returning:", result);

  return result;
}

/**
 * Extract metadata from payload (equivalent to lastCompletedPayload)
 * @param payload - The payload object from API response
 * @returns Metadata object with agentIdName, chatId, requestId, workflowId
 */
export function extractMetadataFromPayload(payload: any): {
  agentIdName?: string;
  chatId?: number;
  requestId?: string;
  workflowId?: string;
} {
  const workflowId = payload?.workflow_id;

  // Handle both structures: payload.result (live) and payload directly (historical)
  const requestId = payload?.requestId || payload?.result?.requestId;

  const chatIdValue = payload?.chat_id || payload?.result?.chat_id;
  const chatId = chatIdValue
    ? typeof chatIdValue === "string"
      ? parseInt(chatIdValue, 10)
      : chatIdValue
    : undefined;

  // Check multiple locations for agent_id
  const agentIdName =
    payload?.data?.agent_id ||
    payload?.result?.data?.agent_id ||
    payload?.data?.agent_name ||
    payload?.agent_id;

  return {
    agentIdName,
    chatId,
    requestId,
    workflowId,
  };
}

/**
 * Main function to parse historical chat message content
 * Handles both campaign workflow and HR workflow responses
 * This replicates the exact logic from userSlice.ts (lines 572-807)
 *
 * API Response Structure:
 * {
 *   status: "completed",
 *   payload: {  // This is equivalent to lastCompletedPayload in userSlice
 *     workflow_id: "...",
 *     agent_id: "press_release_hitl",
 *     result: {
 *       requestId: "...",
 *       chat_id: "7665",
 *       data: {
 *         agent_id: "utility_agent",
 *         question: "",
 *         response: [...]
 *       }
 *     }
 *   }
 * }
 *
 * @param content - Raw message content string from API
 * @param agentEndpoint - Optional agent endpoint to determine workflow type
 * @returns Parsed message content as string (same format as live chat)
 */
export function parseHistoricalMessageContent(
  content: string,
  agentEndpoint?: string,
): string {
  try {
    const parsed = safeJSONParse(content);
    if (!parsed) {
      console.warn("Could not parse content as JSON");
      return content;
    }

    console.log("Parsing content with endpoint:", agentEndpoint);
    console.log("Parsed content structure:", {
      hasStatus: !!parsed.status,
      hasPayload: !!parsed.payload,
      topLevelKeys: Object.keys(parsed),
    });

    // Check if this has the status/payload wrapper from historical API
    let payload: any;
    if (parsed.status && parsed.payload) {
      // Historical API format: {status: "completed", payload: {...}}
      payload = parsed.payload;
      console.log("Using payload from status/payload wrapper");
    } else {
      // Direct payload format
      payload = parsed;
      console.log("Using parsed content directly as payload");
    }

    console.log("Payload structure:", {
      hasResult: !!payload?.result,
      hasData: !!payload?.data,
      hasResultData: !!payload?.result?.data,
      payloadKeys: Object.keys(payload || {}),
      dataKeys: payload?.data ? Object.keys(payload.data) : [],
    });

    // Use same detection logic as userSlice.ts (line 358-361)
    // Check if this is HR workflow endpoint - matching userSlice exactly
    if (isHRWorkflowEndpoint(agentEndpoint)) {
      // Handle HR workflow response - matches userSlice.ts line 544-573
      console.log("Detected HR workflow endpoint, parsing...");
      console.log("Full payload:", payload);

      if (!payload?.data) {
        console.error("HR workflow: payload.data is missing!");
        return content;
      }

      // Pass payload directly (not payload.result) since structure is payload.data
      const hrResult = parseHRWorkflowResponse(payload);
      console.log("HR workflow parsed result:", hrResult);

      if (!hrResult.answer && !hrResult.question) {
        console.error("HR workflow parsing returned empty result!");
        return content;
      }

      return JSON.stringify(hrResult);
    } else {
      // Handle campaign workflow response - matches userSlice.ts line 575-804
      console.log("Detected campaign workflow endpoint, parsing...");
      const campaignResult = parseCampaignWorkflowResponse(payload);
      console.log("Campaign workflow parsed result:", campaignResult);
      return JSON.stringify(campaignResult);
    }
  } catch (error) {
    console.error(
      "Error parsing historical message content:",
      error,
      "Content:",
      content,
    );
    return content;
  }
}

/**
 * Extract all metadata from historical message content
 * @param content - Raw message content string from API
 * @returns Metadata object
 */
export function extractMessageMetadata(content: string): {
  agentIdName?: string;
  chatId?: number;
  requestId?: string;
  workflowId?: string;
} {
  try {
    const parsed = safeJSONParse(content);
    if (!parsed) return {};

    // Check if this has the status/payload wrapper
    let payload: any;
    if (parsed.status && parsed.payload) {
      payload = parsed.payload;
    } else {
      payload = parsed;
    }

    return extractMetadataFromPayload(payload);
  } catch (error) {
    console.error("Error extracting message metadata:", error);
    return {};
  }
}
